import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { checkoutStatus, openBillingPortal } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function BillingPage() {
  const [state, setState] = useState("checking"); // checking | success | failed | timeout | manage
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const polls = useRef(0);

  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (!sid) { setState("manage"); return; }
    let active = true;
    const poll = async () => {
      if (!active) return;
      if (polls.current >= 20) { setState("timeout"); return; }
      polls.current += 1;
      try {
        const r = await checkoutStatus(sid);
        if (r.payment_status === "paid" || r.payment_status === "no_payment_required" || r.status === "complete") {
          await refresh(); setState("success"); return;
        }
        if (r.status === "expired") { setState("failed"); return; }
      } catch { /* keep polling */ }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [refresh]);

  const portal = async () => {
    try {
      const { url } = await openBillingPortal(window.location.origin);
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open the billing portal.");
    }
  };

  const planLabel = user?.plan === "all" ? "All Authorities" : user?.plan === "single" ? "Single Authority" : user?.plan;

  return (
    <div className="min-h-screen bg-[#09090B] grid-texture flex items-center justify-center p-6">
      <div className="max-w-sm w-full border border-zinc-800 rounded-sm bg-[#101010] p-8 text-center" data-testid="billing-result">
        {state === "checking" && (<><Loader2 className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-4" /><h2 className="font-display text-xl font-bold">Confirming payment…</h2><p className="font-mono text-[11px] text-zinc-500 mt-2">This only takes a moment.</p></>)}
        {state === "success" && (<><CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-4" /><h2 className="font-display text-2xl font-bold">Subscription active 🎉</h2><p className="font-mono text-[11px] text-zinc-400 mt-2 mb-6">Your {user?.plan_seats || 1}-seat {planLabel} plan is live. The toolset is unlocked.</p><Button data-testid="billing-continue" onClick={() => nav("/")} className="w-full rounded-sm bg-blue-600 hover:bg-blue-500 text-white mb-2">Start using the app</Button><Button data-testid="billing-portal" onClick={portal} variant="outline" className="w-full rounded-sm border-zinc-700 bg-transparent text-zinc-200"><Settings className="w-4 h-4 mr-2" />Manage subscription</Button></>)}
        {state === "manage" && (
          user?.has_subscription ? (
            <><Settings className="w-10 h-10 text-blue-500 mx-auto mb-4" /><h2 className="font-display text-xl font-bold">Your subscription</h2><p className="font-mono text-[11px] text-zinc-400 mt-2 mb-1">{user?.plan_seats || 1} seat{(user?.plan_seats || 1) > 1 ? "s" : ""} · {planLabel}</p><p className="font-mono text-[10px] text-zinc-600 mb-6">Status: {user?.subscription_status || "active"}</p><Button data-testid="billing-portal" onClick={portal} className="w-full rounded-sm bg-blue-600 hover:bg-blue-500 text-white mb-2"><Settings className="w-4 h-4 mr-2" />Manage subscription</Button><Button onClick={() => nav("/")} variant="outline" className="w-full rounded-sm border-zinc-700 bg-transparent text-zinc-200">Back to app</Button></>
          ) : (
            <><h2 className="font-display text-xl font-bold">No active subscription</h2><p className="font-mono text-[11px] text-zinc-400 mt-2 mb-6">Choose a plan to unlock the full toolset.</p><Button onClick={() => nav("/pricing")} className="w-full rounded-sm bg-blue-600 hover:bg-blue-500 text-white">View plans</Button></>
          )
        )}
        {(state === "failed" || state === "timeout") && (<><XCircle className="w-10 h-10 text-red-500 mx-auto mb-4" /><h2 className="font-display text-xl font-bold">{state === "timeout" ? "Still processing" : "Payment not completed"}</h2><p className="font-mono text-[11px] text-zinc-400 mt-2 mb-6">{state === "timeout" ? "Your payment is taking longer than expected. It will activate automatically once confirmed." : "No charge was made. You can try again anytime."}</p><Button onClick={() => nav("/pricing")} variant="outline" className="w-full rounded-sm border-zinc-700 bg-transparent text-zinc-200">Back to pricing</Button></>)}
      </div>
    </div>
  );
}
